import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  RectangleStackIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import { transformSalesData } from '../../utils/salesDataUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const StockRotationAnalysis = () => {
  const navigate = useNavigate();
  const { products, sales, customers } = useAppData();
  const { companySettings } = useCompanySettings();
  
  const [timeRange, setTimeRange] = useState('last-year');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCalculating, setIsCalculating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Real-time rotation data
  const [rotationData, setRotationData] = useState([]);
  
  // Transform sales data using the shared utility for consistency
  const transformedSalesData = useMemo(() => {
    console.log('🔄 StockRotation: Transforming sales data using shared utility');
    return transformSalesData(sales || [], customers || []);
  }, [sales, customers]);
  
  // Get real categories from products
  const categories = useMemo(() => {
    // Get unique categories from products
    const uniqueCategories = [...new Set(products.filter(p => p.category).map(p => p.category))];
    
    // Create category objects
    return [
    { id: 'all', name: 'Toutes les catégories' },
      ...uniqueCategories.map(cat => ({ id: cat, name: cat }))
  ];
  }, [products]);
  
  // Périodes disponibles
  const timeRanges = [
    { id: 'last-month', name: 'Dernier mois' },
    { id: 'last-quarter', name: 'Dernier trimestre' },
    { id: 'last-6-months', name: 'Derniers 6 mois' },
    { id: 'last-year', name: 'Dernière année' }
  ];
  
  // Calculate product sales from real sales data
  const productSalesData = useMemo(() => {
    console.log('📊 StockRotation: Calculating product sales from real data');
    
    if (!transformedSalesData || transformedSalesData.length === 0) {
      console.log('⚠️ No sales data available');
      return {};
    }
    
    const productSales = {};
    
    // Process all sales to get total sales by product
    transformedSalesData.forEach(sale => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const productId = item.id || item.productId;
          if (!productId) return;
          
          if (!productSales[productId]) {
            productSales[productId] = {
              totalSold: 0,
              totalRevenue: 0,
              lastSaleDate: null
            };
          }
          
          const quantity = parseInt(item.quantity) || 1;
          const total = parseFloat(item.total) || (quantity * (parseFloat(item.price) || 0));
          
          productSales[productId].totalSold += quantity;
          productSales[productId].totalRevenue += total;
          
          // Track last sale date
          const saleDate = new Date(sale.rawDate);
          if (!productSales[productId].lastSaleDate || saleDate > productSales[productId].lastSaleDate) {
            productSales[productId].lastSaleDate = saleDate;
          }
        });
      }
    });
    
    console.log('✅ Product sales calculated:', {
      productCount: Object.keys(productSales).length,
      sample: Object.entries(productSales).slice(0, 2)
    });
    
    return productSales;
  }, [transformedSalesData]);
  
  // Calculate rotation data using real sales data
  useEffect(() => {
    calculateRotationData();
  }, [categoryFilter, timeRange, products, productSalesData]);
  
  const calculateRotationData = () => {
    setIsCalculating(true);
    
    try {
      console.log('🔄 Calculating stock rotation with real data');
      
      // Use real products data
      const productsList = products.length > 0 ? [...products] : [];
      
      // Filter products by category if needed
      const filteredProducts = categoryFilter === 'all' 
        ? productsList 
        : productsList.filter(product => product.category === categoryFilter);
      
      // Calculate rotation rates based on real sales data
      const data = filteredProducts.map(product => {
        // Get real sales data for this product
        const productSales = productSalesData[product.id] || { totalSold: 0, totalRevenue: 0 };
        
        // Calculate rotation rate: Total units sold / Average stock
        const sales = productSales.totalSold || 0;
        const stock = parseInt(product.stock) || 1;
        const rotationRate = sales / stock;
        const stockDays = Math.floor(365 / (rotationRate || 1));
        const turnoverCategory = rotationRate > 6 ? 'high' : rotationRate > 3 ? 'medium' : 'low';
        
        return {
          id: product.id,
          name: product.name,
          category: product.category || 'Non catégorisé',
          rotationRate: parseFloat(rotationRate.toFixed(2)),
          salesVolume: sales,
          revenue: productSales.totalRevenue || 0,
          avgStock: stock,
          stockDays,
          turnoverCategory,
          lastSaleDate: productSales.lastSaleDate
        };
      });
      
      // Sort by rotation rate (highest to lowest)
      const sortedData = [...data].sort((a, b) => b.rotationRate - a.rotationRate);
      
      console.log('✅ Rotation data calculated:', {
        productCount: sortedData.length,
        sample: sortedData.slice(0, 3)
      });
      
      setRotationData(sortedData);
    } catch (error) {
      console.error("Erreur lors du calcul des données de rotation:", error);
      toast.error("Erreur lors du calcul des données de rotation");
    } finally {
      setIsCalculating(false);
    }
  };
  
  // Données pour le graphique de comparaison
  const chartData = rotationData.map(item => ({
    name: item.name.length > 12 ? `${item.name.substring(0, 12)}...` : item.name,
    "Taux de rotation": item.rotationRate,
    "Jours de stock": Math.min(item.stockDays, 120) // Cap à 120 jours pour l'échelle du graphique
  }));
  
  // Statistiques de rotation
  const rotationStats = {
    avgRotation: rotationData.length > 0 
      ? (rotationData.reduce((sum, item) => sum + item.rotationRate, 0) / rotationData.length).toFixed(2)
      : 0,
    highRotationCount: rotationData.filter(item => item.turnoverCategory === 'high').length,
    lowRotationCount: rotationData.filter(item => item.turnoverCategory === 'low').length,
    rotationRange: rotationData.length > 0 
      ? {
          min: Math.min(...rotationData.map(item => item.rotationRate)).toFixed(2),
          max: Math.max(...rotationData.map(item => item.rotationRate)).toFixed(2)
        }
      : { min: 0, max: 0 }
  };
  
  // Format currency with commas (same as reports section)
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0';
    return amount.toLocaleString('en-US').replace(/,/g, ',');
  };
  
  // Generate PDF report
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Stock Rotation PDF generation...');
      console.log('📊 Data check:', {
        rotationDataCount: rotationData?.length || 0,
        chartDataCount: chartData?.length || 0,
        avgRotation: rotationStats.avgRotation,
        highRotationCount: rotationStats.highRotationCount,
        lowRotationCount: rotationStats.lowRotationCount
      });
      
      // Minimalist red theme colors (EXACTLY like Sales/Purchases)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer (EXACTLY like Sales)
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            console.log('📄 Stock Rotation: Adding company logo to PDF...');
            
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.name || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.name || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.name || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title) => {
        if (yPosition > 250) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPosition = 25; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPosition);
        yPosition += 5;
        
        // Simple clean line
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.5);
        pdf.line(20, yPosition, 80, yPosition);
        
        yPosition += 10;
        
        // Reset to black for content
        pdf.setTextColor(0, 0, 0);
        
        return yPosition;
      };
      
      // Add header/footer to first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // 1. MINIMAL TITLE
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('RAPPORT DE ROTATION DES STOCKS', 20, yPosition);
      yPosition += 10;
      
      // Simple date info
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(`${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 15;
      
      // 2. EXECUTIVE SUMMARY
      await addSectionHeader('RÉSUMÉ EXÉCUTIF');
      
      // Summary statistics table
      const summaryData = [
        ['Taux de rotation moyen', `${rotationStats.avgRotation}`],
        ['Produits à rotation élevée', `${rotationStats.highRotationCount}`],
        ['Produits à rotation faible', `${rotationStats.lowRotationCount}`],
        ['Plage de rotation', `${rotationStats.rotationRange.min} - ${rotationStats.rotationRange.max}`]
      ];
      
      console.log('📄 Stock Rotation: Creating summary table with data:', summaryData);
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Métrique', 'Valeur']],
          body: summaryData,
          theme: 'grid',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 10, 
            fontStyle: 'bold',
            halign: 'left'
          },
          columnStyles: {
            0: {
              fontStyle: 'bold',
              cellWidth: 'auto'
            },
            1: {
              cellWidth: 'auto',
              halign: 'center'
            }
          },
          styles: { 
            fontSize: 9, 
            cellPadding: 5,
            lineColor: [220, 220, 220],
            lineWidth: 0.1
          },
          margin: { left: 20, right: 20 }
        });
        console.log('✅ Stock Rotation: Summary table created successfully');
      } catch (tableError) {
        console.error('❌ Stock Rotation: Error creating summary table:', tableError);
        throw tableError;
      }
      
      yPosition = pdf.lastAutoTable.finalY + 20;
      
      // Add section header for detailed data
      await addSectionHeader('DÉTAILS DE ROTATION PAR PRODUIT');
      
      // Rotation details table
      const rotationDetailsData = rotationData
        .map(item => [
          item.name || '',
          item.category || 'Non catégorisé',
          `${item.salesVolume}`,
          `${item.avgStock}`,
          `${item.rotationRate}`,
          `${item.stockDays}`
        ]);
      
      console.log('📄 Stock Rotation: Creating rotation details table');
      try {
        autoTable(pdf, {
          startY: yPosition,
          head: [['Produit', 'Catégorie', 'Ventes', 'Stock Moyen', 'Taux Rotation', 'Jours de Stock']],
          body: rotationDetailsData,
          theme: 'grid',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 10, 
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 'auto', halign: 'center' },
            3: { cellWidth: 'auto', halign: 'center' },
            4: { cellWidth: 'auto', halign: 'center' },
            5: { cellWidth: 'auto', halign: 'center' }
          },
          styles: { 
            fontSize: 9, 
            cellPadding: 5,
            lineColor: [220, 220, 220],
            lineWidth: 0.1,
            valign: 'middle'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { left: 20, right: 20 }
        });
        console.log('✅ Stock Rotation: Rotation details table created successfully');
      } catch (tableError) {
        console.error('❌ Stock Rotation: Error creating rotation details table:', tableError);
        throw tableError;
      }
      
      // Save the PDF
      const fileName = `rapport-rotation-stocks-${new Date().toISOString().split('T')[0]}.pdf`;
      console.log('📄 Stock Rotation: About to save PDF with filename:', fileName);
      pdf.save(fileName);
      console.log('📄 Stock Rotation: PDF save command executed');
      
      toast.success('Rapport PDF généré avec succès');
    } catch (error) {
      console.error('❌ Stock Rotation: Error generating PDF:', error);
      toast.error('Erreur lors de la génération du rapport PDF');
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header with navigation */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/advanced-analytics')}
            className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Analyse de Rotation des Stocks</h1>
            <p className="text-white/60 text-sm mt-1">
              Analysez les taux de rotation et optimisez votre gestion des stocks
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50 shadow-lg"
          >
            <DocumentTextIcon className="h-5 w-5" />
            <span>{isGenerating ? 'Génération...' : 'Exporter PDF Complet'}</span>
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-3">
            <FunnelIcon className="h-5 w-5 text-white/70" />
            <h3 className="text-lg font-medium text-white">Filtres</h3>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Catégorie:</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Période:</span>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {timeRanges.map(range => (
                  <option key={range.id} value={range.id}>
                    {range.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Taux de rotation moyen</p>
              <p className="text-3xl font-bold text-white mt-1">{rotationStats.avgRotation}</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700">
              <ArrowPathIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Cycles de stock complets par an</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Produits à rotation élevée</p>
              <p className="text-3xl font-bold text-green-400 mt-1">{rotationStats.highRotationCount}</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-700">
              <ArrowTrendingUpIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Plus de 6 rotations par an</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Produits à rotation faible</p>
              <p className="text-3xl font-bold text-amber-400 mt-1">{rotationStats.lowRotationCount}</p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-700">
              <ArrowTrendingDownIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Moins de 3 rotations par an</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-white/60 text-sm">Plage de taux de rotation</p>
              <p className="text-3xl font-bold text-white mt-1">
                {rotationStats.rotationRange.min} - {rotationStats.rotationRange.max}
              </p>
            </div>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700">
              <ChartBarIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-white/50">Min - Max rotations par an</p>
        </div>
      </div>
      
      {/* Charts */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-6">Comparaison des taux de rotation</h3>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                stroke="rgba(255,255,255,0.6)" 
                angle={-45}
                textAnchor="end"
                height={70}
              />
              <YAxis stroke="rgba(255,255,255,0.6)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#fff'
                }} 
              />
              <Legend wrapperStyle={{ color: '#fff' }} />
              <Bar dataKey="Taux de rotation" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Table of rotation data */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-semibold text-white">Détails des taux de rotation</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Volume ventes</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock moyen</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Taux rotation</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Jours de stock</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {rotationData.map((item, index) => (
                <tr key={item.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{item.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">{item.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{item.salesVolume}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{item.avgStock}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-white">{item.rotationRate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{item.stockDays}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.turnoverCategory === 'high' ? 'bg-green-100 text-green-800' :
                      item.turnoverCategory === 'medium' ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {item.turnoverCategory === 'high' ? 'Élevée' :
                       item.turnoverCategory === 'medium' ? 'Moyenne' : 'Faible'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recommendations section */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Recommandations</h3>
        
        <div className="space-y-4">
          <div className="flex space-x-4 items-start">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Produits à rotation lente</h4>
              <p className="text-white/70 text-sm mt-1">
                Envisagez de réduire les stocks pour les {rotationStats.lowRotationCount} produits à faible rotation afin de minimiser les coûts de possession.
              </p>
            </div>
          </div>
          
          <div className="flex space-x-4 items-start">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <RectangleStackIcon className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Optimisation des stocks</h4>
              <p className="text-white/70 text-sm mt-1">
                Le taux de rotation moyen de {rotationStats.avgRotation} est {parseFloat(rotationStats.avgRotation) > 5 ? 'bon' : 'inférieur à la cible de 5'}.
                {parseFloat(rotationStats.avgRotation) <= 5 ? ' Envisagez d\'améliorer votre gestion des approvisionnements.' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StockRotationAnalysis; 